import { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { CheckCircle2, Loader2, ArrowRight } from "lucide-react";
import { Seo } from "@/components/Seo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [state, setState] = useState("checking");
  const attempts = useRef(0);

  useEffect(() => {
    if (!sessionId) {
      setState("error");
      return;
    }
    let cancelled = false;
    const poll = async () => {
      while (!cancelled && attempts.current < 20) {
        attempts.current += 1;
        try {
          const res = await fetch(`${API}/payments/status/${sessionId}`);
          if (res.ok) {
            const data = await res.json();
            if (data.payment_status === "paid") {
              if (!cancelled) setState("paid");
              return;
            }
            if (["failed", "expired"].includes(data.payment_status) || ["failed", "expired"].includes(data.status)) {
              if (!cancelled) setState("error");
              return;
            }
          }
        } catch {
          // transient — keep polling
        }
        await new Promise((r) => setTimeout(r, 2000));
      }
      if (!cancelled) setState("timeout");
    };
    poll();
    return () => {
      cancelled = true;
    };
  }, [sessionId]);

  return (
    <main data-testid="payment-success-page" className="flex min-h-[80vh] items-center pt-32 pb-24">
      <Seo title="Payment Confirmed — DISJINX" description="Payment confirmation." path="/payment/success" noindex />
      <div className="mx-auto w-full max-w-2xl px-6 text-center">
        {state === "checking" && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
            <Loader2 className="mx-auto h-10 w-10 animate-spin text-neutral-950" />
            <h1 className="mt-8 font-display text-4xl font-extrabold tracking-tight">Confirming your payment…</h1>
            <p className="mt-4 text-base text-neutral-600">Hold tight — this takes a few seconds.</p>
          </motion.div>
        )}
        {state === "paid" && (
          <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
            <CheckCircle2 className="mx-auto h-12 w-12 text-neutral-950" strokeWidth={1.5} />
            <h1 data-testid="payment-success-heading" className="mt-8 font-display text-4xl font-extrabold tracking-tight lg:text-5xl">
              Welcome aboard.
            </h1>
            <p className="mt-6 text-base leading-relaxed text-neutral-600 md:text-lg">
              Your subscription is active. The DISJINX team will reach out shortly to kick off your automation journey.
              A receipt is on its way to your inbox.
            </p>
            <div className="mt-10 flex flex-wrap justify-center gap-4">
              <Link to="/" data-testid="payment-success-home-link" className="inline-flex items-center gap-2 bg-neutral-950 px-7 py-4 text-sm font-semibold text-white transition-colors duration-200 hover:bg-neutral-800">
                Back to home
                <ArrowRight className="h-4 w-4" />
              </Link>
              <Link to="/contact" data-testid="payment-success-contact-link" className="inline-flex items-center gap-2 border border-neutral-300 px-7 py-4 text-sm font-semibold transition-colors duration-200 hover:border-neutral-950">
                Talk to us
              </Link>
            </div>
          </motion.div>
        )}
        {(state === "error" || state === "timeout") && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
            <h1 data-testid="payment-error-heading" className="font-display text-4xl font-extrabold tracking-tight">
              {state === "timeout" ? "Still confirming…" : "We couldn't confirm that payment."}
            </h1>
            <p className="mt-6 text-base leading-relaxed text-neutral-600">
              If money left your account, don't worry — email us at disjinx.support@gmail.com and we'll sort it out right away.
            </p>
            <Link to="/" data-testid="payment-error-home-link" className="mt-10 inline-flex items-center gap-2 bg-neutral-950 px-7 py-4 text-sm font-semibold text-white transition-colors duration-200 hover:bg-neutral-800">
              Back to home
              <ArrowRight className="h-4 w-4" />
            </Link>
          </motion.div>
        )}
      </div>
    </main>
  );
}
